import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Avatar,
  Button,
  Box,
  CircularProgress,
  Dialog,
  Typography,
} from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { useAuth } from '../../hooks/useAuth';
import UserCourseData from './userCourseData';
import CallToAction from '../landingPage/callToAction';
import TabSection from './TabSection';
// import MvAiReplies from './MvAiReplies';
import MyPosts from './MyPosts';
import MyReplies from './MyReplies';
import {
  AblyWebsocketChannelProvider
} from '../../component/AblyWebsockets/AblyWebsocketChannelProvider';
import { useChannel } from 'ably/react';
import { PostType, CommentType } from '../memeverse/memeverse_types';
import CreateUpdateComment from '../memeverse/CreateUpdateComment';
import { useTheme } from '@mui/material/styles';

const FanProfilePage = () => {
  return (
    <AblyWebsocketChannelProvider channelName='ask'>
      <FanProfile />
    </AblyWebsocketChannelProvider>
  );
};

const FanProfile = () => {
  const { authUser, authLoading } = useAuth();
  const { publish } = useChannel('ask');
  const theme = useTheme();

  const {
    fullName = '',
    profilePicUrl = '',
    userName = '',
    // index = '',
    // shortBio = '',
    // subscription,
  } = authUser || {};

  const [activeTab, setActiveTab] = useState('profile');
  const [replyingTo, setReplyingTo] = useState<PostType | CommentType | null>(
    null
  );

  useEffect(() => {
    if (replyingTo) {
      publish('comment.thinking.new', {
        userId: authUser?.uid,
        parentId: replyingTo.docId,
        level: 'comments_counts' in replyingTo ? 0 : 1,
        userType: 'user',
        user_details: {
          userName: authUser?.userName,
          profilePicUrl: authUser?.profilePicUrl,
          fullName: authUser?.fullName,
          user_id: authUser?.uid,
        },
      });
    }
    return () => {
      if (replyingTo) {
        publish('comment.thinking.remove', {
          userId: authUser?.uid,
          parentId: replyingTo.docId,
          level: 'level' in replyingTo ? replyingTo.level + 1 : 0,
          userType: 'user',
        });
      }
    };
  }, [replyingTo, authUser, publish]);

  return (
    <Box
      flex={1}
      display='flex'
      alignItems='center'
      flexDirection='column'
      mt={5}
    >
      {authLoading ? (
        <Box
          mt={2}
          sx={{
            display: 'flex',
            flex: 1,
            alignItems: 'center',
            flexDirection: 'column',
            justifyContent: 'center',
          }}
        >
          <CircularProgress size={30} />
        </Box>
      ) : (
        <Box display='flex' flexDirection='column' width='100%'>
          {/* Avatar + Name + Username */}
          <Box
            display='flex'
            alignItems='center'
            flexDirection={{ xs: 'column', sm: 'row' }}
            gap={2}
          >
            {/* Avatar */}
            <Avatar
              src={profilePicUrl}
              alt={fullName}
              sx={{
                width: '204px',
                height: '193.21px',
                border: '1px solid #fff',
                borderRadius: '86px',
                objectFit: 'cover',
              }}
            >
              {fullName ? fullName[0] : ''}
            </Avatar>

            {/* Name & Username */}
            <Box
              display='flex'
              flexDirection='column'
              textAlign={{ xs: 'center', sm: 'left' }}
            >
              <Box
                flexDirection='column'
                textAlign={{ xs: 'center', sm: 'left' }}
                width='100%'
              >
                <Typography component='h6' variant='h6' fontWeight={600}>
                  {fullName}
                </Typography>
              </Box>
              <Typography component='h6' variant='body1' color='text.secondary'>
                @{userName}
              </Typography>
              <Link to={`/update-profile`} style={{ flex: 0 }}>
                <Button
                  id={`button-edit-info-${userName}`}
                  variant='contained'
                  size='large'
                  color='secondary'
                  startIcon={<EditOutlinedIcon />}
                  sx={{
                    width: '153px',
                    height: '42px',
                    borderRadius: 'borderRadiusRound',
                    border: '1px solid',
                    borderColor: 'secondary.contrastText',
                    padding: '8px 22px',
                    mt: { xs: 2, sm: 12 },
                    backgroundColor: 'secondary.main',
                    color: 'rgba(255, 255, 255, 1)',
                    boxShadow: `
                      0px 3px 1px -2px rgba(0, 0, 0, 0.2),
                      0px 2px 2px 0px rgba(0, 0, 0, 0.14),
                      0px 1px 5px 0px rgba(0, 0, 0, 0.12)
                    `,
                    '&:hover': {
                      backgroundColor: 'secondary.dark',
                    },
                    whiteSpace: 'nowrap',
                  }}
                >
                  Edit Profile
                </Button>
              </Link>
            </Box>
          </Box>

          <TabSection activeTab={activeTab} setActiveTab={setActiveTab} />

          {activeTab === 'profile' && (
            <>
              <Box width='100%' mt={2}></Box>
              <UserCourseData authUser={authUser} profileData={authUser} />
              <CallToAction />
            </>
          )}

            <>
              {activeTab === 'posts' && (
                <MyPosts
                  userId={authUser.uid}
                  onReplyClick={(item) => setReplyingTo(item)}
                />
              )}

              {activeTab === 'replies' && (
                <MyReplies
                  userId={authUser.uid}
                  onReplyClick={(item) => setReplyingTo(item)}
                />
              )}

              {/* will make this work in future */}
              {/* {activeTab === 'aiReplies' && <MvAiReplies />} */}
            </>
        </Box>
      )}
      {replyingTo && (
        <Dialog
          open={!!replyingTo}
          onClose={() => setReplyingTo(null)}
          fullWidth
          maxWidth='md'
          slotProps={{
            backdrop: {
              sx: {
                display: { xs: 'none', md: 'block' },
              },
            },
          }}
          PaperProps={{
            sx: {
              m: 0,
              [theme.breakpoints.down('md')]: {
                width: '100%',
                maxWidth: '100%',
                height: 'calc(100% - 107px)',
                maxHeight: 'none',
                boxShadow: 'none',
                mt: '50px',
              },
            },
          }}
          sx={{
            [theme.breakpoints.down('md')]: {
              '& .MuiDialog-container': {
                alignItems: 'flex-start',
              },
            },
          }}
          onClick={(event) => {
            event.stopPropagation();
          }}
        >
          <CreateUpdateComment
            setCreateUpdateCommentVisible={() => setReplyingTo(null)}
            parentPostOrComment={replyingTo}
          />
        </Dialog>
      )}
    </Box>
  );
}

export default FanProfilePage;
